import React, { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';

export interface MessageInputProps {
  value: string;
  onChange: (value: string) => void;
  onSend: (text: string) => void;
  disabled?: boolean;
  /** Rendered inside the bordered box above the textarea (e.g. attachment chips). */
  topSlot?: React.ReactNode;
  /** Optional controls rendered left of the Send button (attach, mic, …). */
  actionsSlot?: React.ReactNode;
  className?: string;
}

export const MessageInput: React.FC<MessageInputProps> = ({
  value,
  onChange,
  onSend,
  disabled = false,
  topSlot,
  actionsSlot,
  className,
}) => {
  const { t } = useTranslation();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const submit = useCallback(() => {
    const trimmed = value.trim();
    if (!trimmed || disabled) return;
    onSend(trimmed);
  }, [disabled, onSend, value]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        submit();
      }
    },
    [submit]
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      onChange(e.target.value);
      const textarea = textareaRef.current;
      if (textarea) {
        textarea.style.height = 'auto';
        textarea.style.height = `${Math.min(textarea.scrollHeight, 116)}px`;
      }
    },
    [onChange]
  );

  const canSend = value.trim().length > 0 && !disabled;

  return (
    <div
      className={`w-full min-w-0 shrink-0 bg-surface border-t border-border-default px-3.5 py-2 ${className ?? ''}`}
      data-testid="esa-message-input"
    >
      {/* Inner box — matches design: border-radius 8px, border 1.5px, padding 9 9 8 12, gap 8px */}
      <div className="flex w-full min-w-0 flex-col gap-2 rounded-[8px] border-[1.5px] border-border-default bg-surface pt-[9px] pr-[9px] pb-[8px] pl-3 transition-colors">
        {topSlot}
        <textarea
          ref={textareaRef}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={t('esa.askPlaceholder', 'Ask ESA…')}
          disabled={disabled}
          rows={2}
          aria-label={t('esa.askPlaceholder', 'Ask ESA…')}
          data-testid="esa-message-input-field"
          className="w-full min-w-0 resize-none border-none bg-transparent p-0 text-[14px] font-normal leading-[1.5] text-text-primary outline-none placeholder:text-text-disabled disabled:opacity-50 min-h-[40px] max-h-[116px] overflow-y-auto"
        />

        {/* Action bar — gap 2px, matching design */}
        <div className="flex items-center gap-[2px]">
          {actionsSlot}
          <div className="flex-1 min-w-0" />
          {/* Send — paper-plane icon, opacity-40 when idle, full opacity when active */}
          <button
            type="button"
            onClick={submit}
            disabled={!canSend}
            aria-label={t('esa.sendMessage', 'Send message')}
            className={`inline-flex shrink-0 items-center justify-center w-[32px] h-[32px] rounded-[8px] bg-esa-accent text-white transition-opacity cursor-pointer disabled:cursor-not-allowed ${canSend ? 'opacity-100 hover:bg-esa-hover' : 'opacity-40'}`}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};
